import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Star, Edit, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RoomPreferencesTab() {
  const [roomPreferences, setRoomPreferences] = useState([]);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [showPreferenceDialog, setShowPreferenceDialog] = useState(false);
  const [editingPreference, setEditingPreference] = useState(null);
  const [preferenceForm, setPreferenceForm] = useState({
    employee_number: "", employee_name: "", preferred_room: "", preferred_floor: "", preferred_bed_type: "", notes: ""
  });

  useEffect(() => {
    fetchRoomPreferences();
  }, []);

  const fetchRoomPreferences = async () => {
    setLoadingPreferences(true);
    try {
      const response = await axios.get(`${API}/admin/employee-preferences`);
      setRoomPreferences(response.data);
    } catch (error) {
      console.error("Failed to load room preferences:", error);
    } finally {
      setLoadingPreferences(false);
    }
  };

  const handleCreatePreference = async () => {
    if (!preferenceForm.employee_number) { toast.error("Please enter employee number"); return; }
    try {
      await axios.post(`${API}/admin/employee-preferences`, preferenceForm);
      toast.success("Room preference saved");
      setShowPreferenceDialog(false);
      setPreferenceForm({ employee_number: "", employee_name: "", preferred_room: "", preferred_floor: "", preferred_bed_type: "", notes: "" });
      fetchRoomPreferences();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save preference"); }
  };

  const handleUpdatePreference = async () => {
    try {
      await axios.put(`${API}/admin/employee-preferences/${editingPreference.employee_number}`, preferenceForm);
      toast.success("Preference updated");
      setShowPreferenceDialog(false);
      setEditingPreference(null);
      fetchRoomPreferences();
    } catch (error) { toast.error("Failed to update preference"); }
  };

  const handleDeletePreference = async (employeeNumber) => {
    if (!confirm("Are you sure you want to delete this room preference?")) return;
    try {
      await axios.delete(`${API}/admin/employee-preferences/${employeeNumber}`);
      toast.success("Preference deleted");
      fetchRoomPreferences();
    } catch (error) { toast.error("Failed to delete preference"); }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight">Room Preferences</h1>
          <p className="text-vault-text-secondary font-manrope mt-1">Assign preferred rooms to recurring railroad employees</p>
        </div>
        <Button onClick={() => { setEditingPreference(null); setPreferenceForm({ employee_number: "", employee_name: "", preferred_room: "", preferred_floor: "", preferred_bed_type: "", notes: "" }); setShowPreferenceDialog(true); }} className="vault-btn-primary" data-testid="add-preference-btn">
          <Plus className="w-4 h-4 mr-2" />Add Preference
        </Button>
      </div>

      <Card className="bg-blue-500/10 border-blue-500/30 mb-6">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Star className="w-5 h-5 text-blue-500 mt-0.5" />
            <div>
              <p className="text-blue-400 font-semibold">How Room Preferences Work:</p>
              <ul className="text-vault-text-secondary text-sm mt-1 space-y-1">
                <li>- Assign a preferred room, floor, or bed type to recurring employees</li>
                <li>- When they check in (via Bitsy or form), their preference is suggested</li>
                <li>- If preferred room is occupied, Bitsy suggests alternatives on same floor</li>
                <li>- Employees can still choose a different room if they prefer</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-vault-surface border-vault-border">
        <CardHeader>
          <CardTitle className="text-vault-text flex items-center gap-2">
            <Star className="w-5 h-5 text-vault-gold" />Employee Room Preferences
            <span className="text-vault-text-secondary text-sm font-normal ml-2">({roomPreferences.length} total)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingPreferences ? (
            <div className="text-center py-8"><div className="w-6 h-6 border-2 border-vault-gold border-t-transparent rounded-full animate-spin mx-auto"></div></div>
          ) : roomPreferences.length === 0 ? (
            <div className="text-center py-8 text-vault-text-secondary">
              <Star className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No room preferences set yet</p><p className="text-sm mt-1">Add preferences for recurring employees</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee ID</TableHead><TableHead>Name</TableHead><TableHead>Preferred Room</TableHead>
                  <TableHead>Floor</TableHead><TableHead>Bed Type</TableHead><TableHead>Notes</TableHead><TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roomPreferences.map((pref) => (
                  <TableRow key={pref.employee_number}>
                    <TableCell className="font-mono">{pref.employee_number}</TableCell>
                    <TableCell>{pref.employee_name || '-'}</TableCell>
                    <TableCell>{pref.preferred_room ? <span className="bg-vault-gold/20 text-vault-gold px-2 py-1 rounded font-semibold">Room {pref.preferred_room}</span> : '-'}</TableCell>
                    <TableCell>{pref.preferred_floor ? `Floor ${pref.preferred_floor}` : '-'}</TableCell>
                    <TableCell className="capitalize">{pref.preferred_bed_type || '-'}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{pref.notes || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingPreference(pref); setPreferenceForm({ employee_number: pref.employee_number, employee_name: pref.employee_name || "", preferred_room: pref.preferred_room || "", preferred_floor: pref.preferred_floor || "", preferred_bed_type: pref.preferred_bed_type || "", notes: pref.notes || "" }); setShowPreferenceDialog(true); }} data-testid={`edit-pref-${pref.employee_number}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeletePreference(pref.employee_number)} className="text-red-500 hover:text-red-700" data-testid={`delete-pref-${pref.employee_number}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showPreferenceDialog} onOpenChange={setShowPreferenceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPreference ? 'Edit Room Preference' : 'Add Room Preference'}</DialogTitle>
            <DialogDescription>Set room preferences for a recurring employee</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Employee Number *</Label><Input value={preferenceForm.employee_number} onChange={(e) => setPreferenceForm({ ...preferenceForm, employee_number: e.target.value.replace(/\D/g, '') })} placeholder="e.g., 12345" disabled={editingPreference !== null} data-testid="pref-employee-number-input" /></div>
            <div><Label>Employee Name (optional)</Label><Input value={preferenceForm.employee_name} onChange={(e) => setPreferenceForm({ ...preferenceForm, employee_name: e.target.value })} placeholder="e.g., John Smith" data-testid="pref-employee-name-input" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Preferred Room</Label><Input value={preferenceForm.preferred_room} onChange={(e) => setPreferenceForm({ ...preferenceForm, preferred_room: e.target.value })} placeholder="e.g., 101" data-testid="pref-room-input" /></div>
              <div><Label>Preferred Floor</Label><select value={preferenceForm.preferred_floor} onChange={(e) => setPreferenceForm({ ...preferenceForm, preferred_floor: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background" data-testid="pref-floor-select"><option value="">Any floor</option><option value="1">1st Floor</option><option value="2">2nd Floor</option></select></div>
              <div><Label>Bed Type</Label><select value={preferenceForm.preferred_bed_type} onChange={(e) => setPreferenceForm({ ...preferenceForm, preferred_bed_type: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background" data-testid="pref-bed-select"><option value="">Any type</option><option value="single">Single</option><option value="double">Double</option></select></div>
            </div>
            <div><Label>Notes (optional)</Label><Input value={preferenceForm.notes} onChange={(e) => setPreferenceForm({ ...preferenceForm, notes: e.target.value })} placeholder="e.g., Prefers quiet room away from elevator" data-testid="pref-notes-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreferenceDialog(false)}>Cancel</Button>
            <Button onClick={editingPreference ? handleUpdatePreference : handleCreatePreference} className="vault-btn-primary" data-testid="save-preference-btn">{editingPreference ? 'Update Preference' : 'Save Preference'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
